// Global Instructions Rule Applied!
import React, { useMemo, forwardRef, memo, useRef, useEffect, useState, useCallback } from 'react'
import JoditEditor from 'jodit-react'
import { Jodit } from 'jodit'
import { ShadowPortal } from './ShadowRichHtml.tsx'

/** Keep in sync with `jodit` in package.json so toolbar CSS matches the runtime bundle. */
const JODIT_STYLESHEET_HREFS = ['https://cdn.jsdelivr.net/npm/jodit@4.6.2/es2021/jodit.min.css']

// ---------------- helpers ----------------
function toArray(v) {
  return Array.isArray(v) ? v : v ? [v] : []
}
function prettyKey(k) {
  return String(k)
    .replace(/cmd/i, '⌘')
    .replace(/control|ctrl/i, 'Ctrl')
    .replace(/alt/i, 'Alt')
    .replace(/shift/i, 'Shift')
    .replace(/\s*\+\s*/g, '+')
    .toUpperCase()
}

// Fallback map in case commandToHotkeys isn’t visible on editor.options
const INLINE_SHORTCUTS_FALLBACK = {
  bold: ['ctrl+b', 'cmd+b'],
  italic: ['ctrl+i', 'cmd+i'],
  underline: ['ctrl+u', 'cmd+u'],
  strikethrough: ['ctrl+shift+s', 'cmd+shift+s'],
  superscript: ['ctrl+shift+=', 'cmd+shift+='],
  subscript: ['ctrl+shift+-', 'cmd+shift+-']
}

function hotkeyLabel(editor, key) {
  const map = (editor && editor.options && editor.options.commandToHotkeys) || {}
  const fromOptions = toArray(map[key])
  const keys = fromOptions.length ? fromOptions : toArray(INLINE_SHORTCUTS_FALLBACK[key])
  return keys.length ? keys.map(prettyKey).join(' / ') : ''
}

// (Optional) CSS-like presets if you want "preview" styling in the dropdown (wired via CSS below)
const HEADING_PREVIEW_CLASS = {
  applyH1: 'jd-h1',
  applyH2: 'jd-h2',
  applyH3: 'jd-h3',
  applyQuote: 'jd-quote'
}
// -----------------------------------------

/**
 * Reusable Rich Text Editor Component
 */
/**
 * @typedef {object} RichTextEditorProps
 * @property {string} [value]
 * @property {(content: string) => void} [onChange]
 * @property {(content: string) => void} [onLiveChange]
 * @property {string} [placeholder]
 * @property {boolean} [readonly]
 * @property {string} [className]
 * @property {React.CSSProperties} [style]
 * @property {Record<string, any>} [config]
 * @property {(e: React.KeyboardEvent) => void} [onKeyDown]
 */
/** Stable default so a consumer that passes no config never changes the config identity. */
const EMPTY_CONFIG = {}

const RichTextEditor = memo(
  forwardRef(
    /**
     * @param {RichTextEditorProps} props
     * @param {React.ForwardedRef<any>} ref
     */
    (
      {
        value = '',
        onChange,
        onLiveChange,
        placeholder = '',
        readonly = false,
        className = '',
        style = {},
        config = EMPTY_CONFIG,
        onKeyDown
      },
      ref
    ) => {
      const editorContainerRef = useRef(null)
      const joditInstanceRef = useRef(null)
      const [joditShadowRoot, setJoditShadowRoot] = useState(null)
      const handleShadowRootReady = useCallback((root) => {
        setJoditShadowRoot(root)
      }, [])

      // jodit-react destroys and re-creates the editor whenever its config
      // object changes identity, which drops focus and resets the content to
      // the `value` prop. The callbacks therefore live in refs so neither the
      // config nor the handlers handed to JoditEditor depend on them.
      const onChangeRef = useRef(onChange)
      const onLiveChangeRef = useRef(onLiveChange)
      const onKeyDownRef = useRef(onKeyDown)
      onChangeRef.current = onChange
      onLiveChangeRef.current = onLiveChange
      onKeyDownRef.current = onKeyDown

      // Blur-driven content commit
      const handleContentChange = useCallback((newContent) => {
        onChangeRef.current?.(newContent)
      }, [])

      // Live content changes — fires on input (Jodit 'change'), not just on
      // blur. Consumers use it to keep their working value current while the
      // user types.
      const handleLiveChange = useCallback((newContent) => {
        onLiveChangeRef.current?.(newContent)
      }, [])

      const joditConfig = useMemo(() => {
        const staticConfig = {
          readonly,
          toolbar: true,
          spellcheck: true,
          language: 'en',
          toolbarButtonSize: 'middle',
          showCharsCounter: false,
          showWordsCounter: false,
          showXPathInStatusbar: false,
          askBeforePasteHTML: false,
          askBeforePasteFromWord: false,
          height: '100%',
          minHeight: 200,

          // Use Jodit's styled tooltips (instead of native browser titles)
          showTooltip: true,
          useNativeTooltip: false,

          // Shortcuts (used by tooltips and your own handlers)
          commandToHotkeys: {
            undo: ['cmd+z'],
            redo: ['cmd+y', 'cmd+shift+z'],
            bold: ['cmd+b'],
            italic: ['cmd+i'],
            underline: ['cmd+u'],
            strikethrough: ['cmd+shift+s'],
            superscript: ['cmd+shift+='],
            subscript: ['cmd+shift+-'],
            removeFormat: ['cmd+shift+m'],

            // Headings + Lists
            h1: ['cmd+alt+1'],
            h2: ['cmd+alt+2'],
            h3: ['cmd+alt+3'],
            blockquote: ['cmd+alt+q'],
            insertOrderedList: ['cmd+alt+n'],
            insertUnorderedList: ['cmd+alt+b'],

            openSearchDialog: ['ctrl+f', 'cmd+f'],
            openReplaceDialog: ['ctrl+r', 'cmd+r']
          },

          // Use the new headings dropdown (styled tooltips), not the old 'paragraph'
          buttons: [
            'headings',
            'inlineStyles',
            'font',
            'fontsize',
            'brush',
            '|',
            'outdent',
            'indent',
            'insert',
            '|',
            'undo',
            'redo'
          ],
          buttonsMD: [
            'headings',
            'inlineStyles',
            'font',
            'fontsize',
            'brush',
            '|',
            'insert',
            'outdent',
            'indent',
            '|',
            'undo',
            'redo'
          ],
          buttonsSM: [
            'headings',
            'inlineStyles',
            'font',
            'fontsize',
            'brush',
            '|',
            'insert',
            'outdent',
            'indent',
            '|',
            'undo',
            'redo'
          ],
          buttonsXS: ['headings', 'inlineStyles', 'font', 'fontsize', 'brush', 'insert'],

          controls: {
            /**
             * INLINE STYLES: keep using native child buttons and override their tooltips to show shortcuts.
             */
            inlineStyles: {
              tooltip: 'Inline styles',
              icon: 'bold',
              list: Jodit.atom(['bold', 'italic', 'underline', 'strikethrough', 'superscript', 'subscript'])
            },
            bold: { tooltip: (j) => hotkeyLabel(j, 'bold') || j.i18n('Bold') },
            italic: { tooltip: (j) => hotkeyLabel(j, 'italic') || j.i18n('Italic') },
            underline: { tooltip: (j) => hotkeyLabel(j, 'underline') || j.i18n('Underline') },
            strikethrough: { tooltip: (j) => hotkeyLabel(j, 'strikethrough') || j.i18n('Strike through') },
            superscript: { tooltip: (j) => hotkeyLabel(j, 'superscript') || j.i18n('superscript') },
            subscript: { tooltip: (j) => hotkeyLabel(j, 'subscript') || j.i18n('subscript') },

            /**
             * LIST BUTTONS: show shortcuts in their tooltips as well (toolbar + any lists).
             */
            ul: { tooltip: (j) => hotkeyLabel(j, 'insertUnorderedList') || j.i18n('Bulleted list') },
            ol: { tooltip: (j) => hotkeyLabel(j, 'insertOrderedList') || j.i18n('Ordered list') },

            /**
             * HEADINGS DROPDOWN: child items are real controls (-> native Jodit tooltips).
             * We keep text labels; optional CSS below can make them appear as large "previews".
             */
            applyH1: {
              name: 'applyH1',
              text: 'Heading 1',
              tooltip: (j) => hotkeyLabel(j, 'h1') || 'Ctrl+Alt+1 / ⌘⌥1',
              exec: (editor) => editor.execCommand('formatBlock', false, 'H1'),
              // add a class for optional preview styling inside dropdown
              className: HEADING_PREVIEW_CLASS.applyH1
            },
            applyH2: {
              name: 'applyH2',
              text: 'Heading 2',
              tooltip: (j) => hotkeyLabel(j, 'h2') || 'Ctrl+Alt+2 / ⌘⌥2',
              exec: (editor) => editor.execCommand('formatBlock', false, 'H2'),
              className: HEADING_PREVIEW_CLASS.applyH2
            },
            applyH3: {
              name: 'applyH3',
              text: 'Heading 3',
              tooltip: (j) => hotkeyLabel(j, 'h3') || 'Ctrl+Alt+3 / ⌘⌥3',
              exec: (editor) => editor.execCommand('formatBlock', false, 'H3'),
              className: HEADING_PREVIEW_CLASS.applyH3
            },
            applyQuote: {
              name: 'applyQuote',
              text: 'Quote',
              tooltip: (j) => hotkeyLabel(j, 'blockquote') || 'Ctrl+Alt+Q / ⌘⌥q', // add to commandToHotkeys if you wire a real hotkey
              exec: (editor) => editor.execCommand('formatBlock', false, 'blockquote'),
              className: HEADING_PREVIEW_CLASS.applyQuote
            },
            applyUL: {
              name: 'applyUL',
              text: 'Bullet List',
              icon: 'ul', // uses Jodit’s list icon if available
              tooltip: (j) => hotkeyLabel(j, 'insertUnorderedList') || 'Ctr+Alt+b / ⌘⌥b',
              exec: (editor) => editor.execCommand('insertUnorderedList')
            },
            applyOL: {
              name: 'applyOL',
              text: 'Numbered List',
              icon: 'ol',
              tooltip: (j) => hotkeyLabel(j, 'insertOrderedList') || 'Ctr+Alt+n / ⌘⌥n',
              exec: (editor) => editor.execCommand('insertOrderedList')
            },

            // Parent dropdown hosting the child controls above
            headings: {
              tooltip: 'Paragraph',
              icon: 'paragraph',
              // Use control names so Jodit renders real buttons with native tooltips
              list: Jodit.atom({
                applyH1: 'Heading 1',
                applyH2: 'Heading 2',
                applyH3: 'Heading 3',
                applyQuote: 'Quote',
                applyUL: 'Bullet List',
                applyOL: 'Numbered List'
              })
              // No childTemplate needed; we'll style via CSS classes for preview (below)
            },

            // Custom controls for insert dropdown
            hr: {
              name: 'hr',
              text: 'Horizontal Line',
              tooltip: 'Insert horizontal line',
              exec: (editor) => editor.execCommand('insertHorizontalRule')
            },

            specialchar: {
              name: 'specialchar',
              text: 'Special Character',
              tooltip: 'Insert special character',
              exec: (editor) => {
                // Simple implementation - could be enhanced with a character picker
                const char = prompt('Enter special character:')
                if (char) {
                  editor.selection.insertHTML(char)
                }
              }
            },

            emoji: {
              name: 'emoji',
              text: 'Emoji',
              tooltip: 'Insert emoji',
              exec: (editor) => {
                // Simple implementation - could be enhanced with emoji picker
                const emojis = ['😀', '😊', '😍', '🤔', '👍', '❤️', '🎉', '🔥']
                const selectedEmoji = prompt(
                  `Choose emoji (enter number 1-${emojis.length}):\n${emojis.map((e, i) => `${i + 1}. ${e}`).join('\n')}`
                )
                const index = parseInt(selectedEmoji) - 1
                if (!isNaN(index) && index >= 0 && index < emojis.length) {
                  editor.selection.insertHTML(emojis[index])
                }
              }
            },

            // Insert dropdown menu
            insert: {
              tooltip: 'Insert',
              icon: 'plus',
              list: Jodit.atom({
                table: 'Table',
                hr: 'Horizontal Line',
                link: 'Link',
                unlink: 'Unlink'
              })
            }
          },

          style: {
            font: '14px/1.6 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            color: '#374151'
          },
          removeButtons: ['print', 'about'],
          showPlaceholder: false,
          placeholder: placeholder || '',

          events: {
            afterInit: (editor) => {
              joditInstanceRef.current = editor

              // Make only the editor's editable area focusable (not the container)
              const editorElement = editor.container
              if (editorElement) {
                // Remove tabIndex from container to prevent it from being focused
                editorElement.removeAttribute('tabindex')

                // Find the actual editable element (could be iframe or contentEditable)
                const editableArea = editorElement.querySelector('[contenteditable="true"], iframe')
                if (editableArea) {
                  editableArea.setAttribute('tabindex', '0')
                }
              }

              // Optional: runtime hotkeys for headings/lists (kept from your version)
              editor.events.on('keydown', (event) => {
                // Forward onKeyDown handler if provided
                const onKeyDown = onKeyDownRef.current
                if (onKeyDown) {
                  // Convert Jodit event to React KeyboardEvent-like object
                  const reactEvent = {
                    key: event.key,
                    code: event.code,
                    ctrlKey: event.ctrlKey,
                    metaKey: event.metaKey,
                    altKey: event.altKey,
                    shiftKey: event.shiftKey,
                    preventDefault: () => event.preventDefault(),
                    stopPropagation: () => event.stopPropagation(),
                    target: event.target,
                    currentTarget: event.currentTarget
                  }
                  onKeyDown(reactEvent)
                }

                if ((event.ctrlKey || event.metaKey) && (event.altKey || event.shiftKey)) {
                  // Use either Alt-number or Shift-7/8 depending on platform habits
                  switch (event.code) {
                    case 'Digit1':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('formatBlock', false, 'H1')
                        return false
                      }
                      break
                    case 'Digit2':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('formatBlock', false, 'H2')
                        return false
                      }
                      break
                    case 'Digit3':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('formatBlock', false, 'H3')
                        return false
                      }
                      break
                    case 'KeyQ':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('formatBlock', false, 'blockquote')
                        return false
                      }
                      break
                    case 'KeyB':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('insertUnorderedList')
                        return false
                      }
                      break
                    case 'KeyN':
                      if (event.altKey) {
                        event.preventDefault()
                        editor.execCommand('insertOrderedList')
                        return false
                      }
                      break
                    default:
                      break
                  }
                }
              })
            },

            beforeDestruct: () => {}
          },

          ...config
        }
        return staticConfig
      }, [config, placeholder, readonly])

      const joditConfigWithShadow = useMemo(
        () => ({
          ...joditConfig,
          ...(joditShadowRoot ? { shadowRoot: joditShadowRoot, globalFullSize: false } : {})
        }),
        [joditConfig, joditShadowRoot]
      )

      const containerStyle = {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        overflow: 'hidden',
        ...style
      }

      // Ensure editor's editable area is focusable after mount (not the container)
      useEffect(() => {
        if (editorContainerRef.current && joditInstanceRef.current) {
          const editorElement = joditInstanceRef.current.container
          if (editorElement) {
            // Remove tabIndex from container - we only want the editable area focusable
            editorElement.removeAttribute('tabindex')

            // Find and make the actual editable element focusable
            const editableArea = editorElement.querySelector('[contenteditable="true"], iframe')
            if (editableArea) {
              editableArea.setAttribute('tabindex', '0')
            }
          }
        }
      }, [value]) // Re-run when value changes to catch re-renders

      return (
        <ShadowPortal
          ref={editorContainerRef}
          className={`jodit-editor-container h-full ${className}`}
          style={containerStyle}
          tabIndex={-1}
          stylesheetHrefs={JODIT_STYLESHEET_HREFS}
          onShadowRootReady={handleShadowRootReady}
        >
          <JoditEditor
            value={value}
            config={joditConfigWithShadow}
            onChange={handleLiveChange}
            onBlur={handleContentChange}
          />
        </ShadowPortal>
      )
    }
  )
)

RichTextEditor.displayName = 'RichTextEditor'
export default RichTextEditor
